import { newId, type SyncOperation, type SyncPushResponse } from '@odr/shared';

import { ApiError, api } from '../api';
import * as store from './db';

/**
 * Sync engine.
 *
 * Contract with the server (see server/src/modules/sync/sync.service.ts):
 *  - every operation carries a client-minted `operationId`; replaying a batch
 *    returns DUPLICATE and changes nothing.
 *  - every entity carries a client-minted id, so "insert if absent" is enough.
 *
 * That means this engine can retry as aggressively as it likes. The only thing
 * it must never do is drop an operation on the floor.
 */

export type SyncState = 'IDLE' | 'SYNCING' | 'OFFLINE' | 'ERROR';

export interface SyncStatus {
  state: SyncState;
  online: boolean;
  pending: number;
  conflicts: number;
  errors: number;
  lastSyncedAt: string | null;
  lastError: string | null;
}

type Subscriber = (status: SyncStatus) => void;

let status: SyncStatus = {
  state: 'IDLE',
  online: navigator.onLine,
  pending: 0,
  conflicts: 0,
  errors: 0,
  lastSyncedAt: null,
  lastError: null,
};

const subscribers = new Set<Subscriber>();
let syncInFlight: Promise<void> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let consecutiveFailures = 0;

export function subscribe(subscriber: Subscriber): () => void {
  subscribers.add(subscriber);
  subscriber(status);
  return () => subscribers.delete(subscriber);
}

export function getStatus(): SyncStatus {
  return status;
}

function emit(patch: Partial<SyncStatus>): void {
  status = { ...status, ...patch };
  for (const subscriber of subscribers) subscriber(status);
}

async function refreshCounts(): Promise<void> {
  const counts = await store.countByStatus();
  const photos = await store.pendingPhotos();
  emit({
    pending: counts.PENDING + counts.SENDING + counts.ERROR + photos.length,
    conflicts: counts.CONFLICT,
    errors: counts.ERROR,
  });
}

/** Queue an operation and try to flush immediately if we are online. */
export async function queue(
  type: SyncOperation['type'],
  entityId: string,
  payload: unknown,
): Promise<void> {
  await store.enqueue({
    operationId: newId(),
    type,
    entityId,
    payload,
    clientTimestamp: new Date().toISOString(),
  });
  await refreshCounts();
  void flush();
}

export async function queuePhoto(photo: {
  id: string;
  runId: string;
  answerId: string | null;
  blob: Blob;
  filename: string;
  mimeType: string;
}): Promise<void> {
  await store.stashPhoto({ ...photo, capturedAt: new Date().toISOString() });
  await refreshCounts();
  void flush();
}

/**
 * Upload photos first.
 *
 * why order matters: an answer's evidence requirement is satisfied by a media
 * row existing on the server. If the run were submitted before its photos
 * landed, the server would reject the submit for missing evidence.
 */
async function flushPhotos(): Promise<void> {
  const photos = await store.pendingPhotos();
  for (const photo of photos) {
    const form = new FormData();
    form.append('file', photo.blob, photo.filename);
    form.append('id', photo.id);
    form.append('capturedAt', photo.capturedAt);

    try {
      await api.upload('/api/media', form);
      await api.post(`/api/media/attach/run/${photo.runId}`, {
        mediaId: photo.id,
        answerId: photo.answerId,
      });
      await store.markPhotoUploaded(photo.id);
    } catch (error) {
      if (error instanceof ApiError && !error.isNetworkError && error.status < 500) {
        // The server refused this file outright (too large, wrong type). Retrying
        // forever would block the whole queue, so drop it and surface the reason.
        await store.dropPhoto(photo.id);
        emit({ lastError: `A photo could not be uploaded: ${error.message}` });
        continue;
      }
      throw error;
    }
  }
}

async function flushOperations(): Promise<void> {
  const pending = await store.pendingOperations();
  if (pending.length === 0) return;

  const clientId = await store.clientId();

  // Chunked so a device returning from a long outage does not send one enormous
  // request that times out and never makes progress.
  const CHUNK = 50;
  for (let i = 0; i < pending.length; i += CHUNK) {
    const batch = pending.slice(i, i + CHUNK);
    for (const op of batch) {
      await store.updateOperation(op.operationId, { status: 'SENDING' });
    }

    const response = await api.post<SyncPushResponse>('/api/sync/push', {
      clientId,
      operations: batch.map(({ operationId, type, entityId, payload, clientTimestamp }) => ({
        operationId,
        type,
        entityId,
        payload,
        clientTimestamp,
      })),
    });

    for (const result of response.results) {
      const original = batch.find((o) => o.operationId === result.operationId);
      if (!original) continue;

      switch (result.status) {
        case 'APPLIED':
        case 'DUPLICATE':
          // Both mean the server has it. Nothing left to send.
          await store.removeOperation(result.operationId);
          break;
        case 'CONFLICT':
          // Keep it, but stop retrying — a human has to look at this.
          await store.updateOperation(result.operationId, {
            status: 'CONFLICT',
            lastError: result.message,
            serverState: result.serverState,
          });
          break;
        case 'ERROR':
        default:
          await store.updateOperation(result.operationId, {
            status: 'ERROR',
            attempts: original.attempts + 1,
            lastError: result.message,
          });
          break;
      }
    }
  }
}

export async function flush(): Promise<void> {
  if (!navigator.onLine) {
    emit({ state: 'OFFLINE', online: false });
    await refreshCounts();
    return;
  }

  // Single-flight: a burst of answers must not start a burst of pushes.
  if (syncInFlight) return syncInFlight;

  syncInFlight = (async () => {
    emit({ state: 'SYNCING', online: true, lastError: null });
    try {
      await flushPhotos();
      await flushOperations();
      await refreshCounts();
      consecutiveFailures = 0;
      emit({ state: 'IDLE', lastSyncedAt: new Date().toISOString() });
    } catch (error) {
      consecutiveFailures += 1;
      const message =
        error instanceof ApiError
          ? error.message
          : 'Could not reach the ODR server. Your work is safe on this device.';

      if (error instanceof ApiError && error.isNetworkError) {
        emit({ state: 'OFFLINE', online: false, lastError: message });
      } else {
        emit({ state: 'ERROR', lastError: message });
      }

      await refreshCounts();
      scheduleRetry();
    } finally {
      syncInFlight = null;
    }
  })();

  return syncInFlight;
}

/** Exponential backoff, capped at 5 minutes. */
function scheduleRetry(): void {
  if (retryTimer) clearTimeout(retryTimer);
  const delay = Math.min(5 * 60_000, 2_000 * 2 ** Math.min(consecutiveFailures, 8));
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flush();
  }, delay);
}

let started = false;

export function startSyncEngine(): () => void {
  if (started) return () => undefined;
  started = true;

  const goOnline = () => {
    emit({ online: true });
    consecutiveFailures = 0;
    void flush();
  };
  const goOffline = () => emit({ state: 'OFFLINE', online: false });

  window.addEventListener('online', goOnline);
  window.addEventListener('offline', goOffline);

  // Coming back to the tab is the most common moment a device regains signal.
  const onVisible = () => {
    if (document.visibilityState === 'visible' && navigator.onLine) void flush();
  };
  document.addEventListener('visibilitychange', onVisible);

  const interval = setInterval(() => {
    if (navigator.onLine) void flush();
  }, 60_000);

  void refreshCounts();
  if (navigator.onLine) void flush();

  return () => {
    window.removeEventListener('online', goOnline);
    window.removeEventListener('offline', goOffline);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(interval);
    if (retryTimer) clearTimeout(retryTimer);
    started = false;
  };
}

/** Discard a conflicted operation once the operator has acknowledged it. */
export async function discardConflict(operationId: string): Promise<void> {
  await store.removeOperation(operationId);
  await refreshCounts();
}

/** Retry a conflicted operation — used after the blocking condition is cleared. */
export async function retryOperation(operationId: string): Promise<void> {
  await store.updateOperation(operationId, { status: 'PENDING', lastError: undefined });
  await refreshCounts();
  void flush();
}

export { store };
